import { COMMERCIAL_ROUNDING, MONEY_SCALE, decimal, divide, formatMoney, fromString, } from '@aeris/money';
import { encodeCode128, encodeEan13, isEan13, } from "./barcode.js";
import { isRollLayout } from "./sheet-geometry.js";
/**
 * Desenhar um modelo posicionado — F124.
 *
 * `html.ts` desenha a etiqueta simples do Aeris, onde os campos se empilham.
 * Este arquivo desenha um MODELO: cada campo no milímetro em que a loja o pôs,
 * com a fonte, o corpo e a cor que ela escolheu. É o que permite abrir um
 * `.conf` do GDOOR e ver sair a mesma etiqueta que eles imprimem hoje.
 *
 * ## Por que milímetro absoluto, e não uma grade
 *
 * Porque foi assim que o modelo foi desenhado. Um campo de preço a 23 mm da
 * esquerda dentro de uma tarja de 41 mm não é "centralizado no rodapé" — é
 * aquele retângulo, naquele lugar, e qualquer aproximação sai visivelmente
 * diferente do adesivo que está colado na prateleira ao lado.
 *
 * Nada aqui lê o DOM: a mesma função gera a pré-visualização e o papel.
 */
const escapeHtml = (value) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
const mm = (value) => `${String(Math.round(value * 1000) / 1000)}mm`;
const pt = (value) => `${String(Math.round(value * 100) / 100)}pt`;
/** Milímetro para gente ler, com vírgula, como o editor escreve (F264). */
const paperMm = (value) => String(Math.round(value * 1000) / 1000).replace('.', ',');
/**
 * `12X de R$ 15,83` — o preço dividido, em decimal exato (F129).
 *
 * Arredonda meio para cima no centavo, como a maquininha mostra a parcela. O
 * valor impresso é um anúncio: quem cobra é o PDV, com a regra de parcelamento
 * da forma de pagamento. Sem preço, ou sem número de vezes, o campo fica vazio.
 */
export const installmentText = (amount, installments) => {
    if (amount === null || installments === undefined)
        return '';
    const times = Math.trunc(installments);
    if (times < 1)
        return '';
    const share = divide(fromString(amount), decimal(BigInt(times), 0), MONEY_SCALE, COMMERCIAL_ROUNDING);
    return `${String(times)}X de ${formatMoney(share)}`;
};
/** O que um campo mostra para este produto. */
/** O texto de um campo para este produto (F124); o ZPL lê o mesmo (F312). */
export const labelFieldText = (element, subject, context) => {
    if (element.kind !== 'text')
        return '';
    switch (element.source) {
        case 'sku':
            return subject.sku;
        case 'name':
            return subject.name;
        /*
         * Preço ausente é campo VAZIO, nunca "R$ 0,00".
         *
         * Vale aqui a mesma regra de `label.ts`: um produto sem preço cadastrado
         * existe, e o zero impresso é um zero que alguém cobra.
         */
        case 'salePrice':
            return subject.salePrice ?? '';
        case 'lastPurchaseAt':
            return subject.lastPurchaseAt ?? '';
        case 'printedAt':
            return context.printedOn;
        case 'supplierReference':
            return subject.supplierReference ?? '';
        case 'location':
            return subject.location ?? '';
        case 'gtin':
            return subject.gtin ?? '';
        case 'brand':
            return subject.brand ?? '';
        case 'unit':
            return subject.unit ?? '';
        case 'installment':
            return installmentText(subject.salePriceAmount ?? null, element.installments);
        case 'literal':
            return element.text;
    }
};
/**
 * As barras, em unidades de módulo.
 *
 * O `viewBox` conta módulos e o elemento mede milímetros — quem converte é o
 * navegador, com a aritmética exata que ele já tem. Calcular a largura de cada
 * barra em milímetros aqui acumularia arredondamento noventa e cinco vezes, e
 * o símbolo deixaria de ler.
 */
/**
 * O ponto da térmica de 203 dpi (a Zebra ZD421 da Elite), em milímetros.
 *
 * F282: as barras esticadas até a largura da caixa davam módulo de 2,4
 * pontos; o driver arredonda cada barra para 2 ou 3 pontos, e o leitor não lia
 * ("o código de barra não tá lendo"). Com o módulo num número inteiro de
 * pontos toda barra sai com a largura certa — e o símbolo fica centrado na
 * caixa, com a sobra virando margem de silêncio.
 */
const PRINTER_DOT_MM = 25.4 / 203;
/** A largura do símbolo: o maior módulo de pontos inteiros que cabe na caixa. */
export const barcodeWidthMm = (boxWidthMm, modules) => {
    const dots = Math.floor(boxWidthMm / modules / PRINTER_DOT_MM);
    return dots < 1 ? boxWidthMm : modules * dots * PRINTER_DOT_MM;
};
const barcodeSvg = (pattern, color, widthMm) => {
    const rects = [];
    let x = 0;
    pattern.modules.forEach((width, index) => {
        if (index % 2 === 0) {
            rects.push(`<rect x="${String(x)}" y="0" width="${String(width)}" height="100"/>`);
        }
        x += width;
    });
    return [
        `<svg viewBox="0 0 ${String(pattern.totalModules)} 100"`,
        ' preserveAspectRatio="none" shape-rendering="crispEdges"',
        ` style="width:${widthMm === undefined ? '100%' : mm(widthMm)};height:100%;display:block;margin:0 auto;fill:${color}"`,
        ` role="img" aria-label="Código de barras ${escapeHtml(pattern.text)}">`,
        rects.join(''),
        '</svg>',
    ].join('');
};
/**
 * Quem decide a simbologia é o DADO, não o modelo.
 *
 * O `.conf` do GDOOR diz `bcCode128A` para todo mundo, porque lá o campo de
 * barras é sempre o código interno. Aqui, um produto com GTIN-13 válido merece
 * um EAN-13 de verdade: é o que o leitor do PDV procura e o que um marketplace
 * aceita. Sem GTIN, CODE128 do SKU, que o leitor da loja lê igual.
 */
/** As barras deste produto: GTIN válido em EAN-13, senão CODE128 (F124). */
export const labelBarcodeOf = (subject) => {
    const gtin = subject.gtin?.trim() ?? '';
    if (gtin !== '') {
        /*
         * EAN-13 quando o número é um; CODE128 do MESMO número quando não é (F168).
         *
         * Antes caía no SKU, e o adesivo saía com um número diferente do que está
         * escrito embaixo das barras na prateleira. Os 5.531 produtos do sistema
         * antigo têm código de barras sem dígito verificador — é ele que a loja
         * bipa, e é ele que tem de sair impresso.
         */
        if (isEan13(gtin))
            return encodeEan13(gtin);
        return encodeCode128(gtin);
    }
    return subject.sku === '' ? null : encodeCode128(subject.sku);
};
const renderElement = (element, subject, context) => {
    const box = [
        'position:absolute',
        `left:${mm(element.leftMm)}`,
        `top:${mm(element.topMm)}`,
        `width:${mm(element.widthMm)}`,
        `height:${mm(element.heightMm)}`,
    ];
    if (element.kind === 'barcode') {
        const pattern = labelBarcodeOf(subject);
        if (pattern === null)
            return '';
        return [
            `<div class="fld-bars" style="${box.join(';')}">`,
            barcodeSvg(pattern, element.color, barcodeWidthMm(element.widthMm, pattern.totalModules)),
            element.showText
                ? `<div style="text-align:center;font-family:monospace;font-size:6pt">${escapeHtml(pattern.text)}</div>`
                : '',
            '</div>',
        ].join('');
    }
    const text = labelFieldText(element, subject, context);
    // Campo sem valor não vira caixa vazia: uma tarja colorida sem nada dentro é
    // uma mancha que ninguém consegue explicar olhando o adesivo.
    if (text === '')
        return '';
    const style = [
        ...box,
        'display:flex',
        /*
         * O texto começa no TOPO da caixa, não no meio.
         *
         * É o padrão do editor do GDOOR (o `TLabel` do Delphi nasce com
         * `Layout = tlTop`), e a primeira versão deste arquivo centralizava — o que
         * parecia certo e só apareceu com os modelos reais: `ETIQUETAS A4
         * COMPONENTES` desenha o nome numa caixa de 22 mm de altura, e centralizado
         * ele descia até cobrir o preço que está a 13 mm do topo.
         */
        'align-items:flex-start',
        `justify-content:${element.align === 'center' ? 'center' : element.align === 'right' ? 'flex-end' : 'flex-start'}`,
        `text-align:${element.align}`,
        `font-family:${element.fontFamily}, Arial, sans-serif`,
        `font-size:${pt(element.fontSizePt)}`,
        'line-height:1.05',
        `color:${element.color}`,
        element.bold ? 'font-weight:700' : 'font-weight:400',
        element.italic ? 'font-style:italic' : '',
        element.underline ? 'text-decoration:underline' : '',
        element.background === null ? '' : `background:${element.background}`,
        /*
         * `QuebraLinha=0` significa uma linha só, e o que passar fica de fora.
         *
         * É o comportamento do GDOOR, e copiá-lo é o ponto: um nome comprido que
         * lá era cortado e aqui quebrasse em três linhas empurraria o preço para
         * fora do adesivo — a etiqueta ficaria diferente da que está na prateleira.
         */
        /*
         * Código nunca quebra de linha, e pode passar da caixa.
         *
         * Os modelos do GDOOR reservam 8 mm para o código porque lá ele é um número
         * curto. No Aeris o código interno é um SKU como `PRD-7K2M`, e quebrado nos
         * hífens em três linhas ele fica ilegível — quem digita o código na mão lê
         * `PRD-` numa linha e não acha o resto. Passar um pouco da caixa é um
         * defeito visível; quebrar é um defeito que atrapalha a venda.
         */
        element.source === 'sku'
            ? 'white-space:nowrap;overflow:visible'
            : element.wrap
                ? 'white-space:pre-wrap;overflow:hidden'
                : /*
                   * Uma linha só (`QuebraLinha=0`) não quebra SOZINHA, mas respeita a
                   * quebra que o modelo escreveu, e não é cortada na borda da caixa
                   * (F128). No `ETIQUETAS TONY 2024` a garantia tem duas linhas
                   * digitadas e caixa de 37 mm com `AutoWidth=1`: com `nowrap` as duas
                   * viravam uma só, centralizada e cortada dos dois lados — "COBRE:
                   * ARRANHADO, QUEBRADO, M". Quem limita o desenho é o adesivo.
                   */
                    'white-space:pre;overflow:visible',
        element.rotationDeg === 0
            ? ''
            : `transform:rotate(${String(-element.rotationDeg / 10)}deg);transform-origin:center`,
    ].filter((rule) => rule !== '');
    return `<div class="fld${element.bold ? ' b' : ''}" style="${style.join(';')}">${escapeHtml(text)}</div>`;
};
/**
 * Onde fica o adesivo de número `index` (a contar de zero) numa folha (F140).
 *
 * Margem, mais uma coluna e um vão por coluna à esquerda; margem, mais uma
 * fileira e um vão por fileira acima. Cada adesivo é posto no seu milímetro,
 * e não empilhado depois do anterior: empilhar somava o erro de cada fileira
 * na seguinte, e a margem de cima, que era o `padding` do `body`, só valia na
 * primeira folha.
 */
export const labelPosition = (layout, index) => {
    const columns = Math.max(1, layout.columns);
    const column = index % columns;
    const row = Math.floor(index / columns);
    return {
        leftMm: layout.page.marginLeftMm +
            column * (layout.label.widthMm + layout.columnGapMm),
        topMm: layout.page.marginTopMm + row * (layout.label.heightMm + layout.rowGapMm),
    };
};
const cellStyle = (layout, index) => {
    const position = labelPosition(layout, index);
    return [
        'position:absolute',
        `left:${mm(position.leftMm)}`,
        `top:${mm(position.topMm)}`,
        `width:${mm(layout.label.widthMm)}`,
        `height:${mm(layout.label.heightMm)}`,
        'overflow:hidden',
    ].join(';');
};
const renderLabel = (layout, subject, positionIndex, highlight, interactive, context) => [
    `<div class="label" style="${cellStyle(layout, positionIndex)}">`,
    layout.elements
        .map((element, index) => {
        let html = renderElement(element, subject, context);
        /*
         * Na prévia do editor (F129), cada campo carrega o seu índice, para que
         * clicar nele o selecione. Campo sem valor no exemplo vira uma caixa
         * tracejada: sumir da prévia seria sumir do alcance do mouse.
         */
        if (interactive) {
            html =
                html === ''
                    ? `<div data-element="${String(index)}" style="position:absolute;left:${mm(element.leftMm)};top:${mm(element.topMm)};width:${mm(element.widthMm)};height:${mm(element.heightMm)};outline:0.2mm dashed #9ca3af"></div>`
                    : html.replace('<div ', `<div data-element="${String(index)}" `);
        }
        // O campo que o editor está mexendo, com contorno. Só na prévia:
        // quem imprime nunca passa `highlight`.
        return index === highlight
            ? html.replace('style="', 'style="outline:0.3mm dashed #2563eb;')
            : html;
    })
        .join(''),
    '</div>',
].join('');
/**
 * O CSS da folha: a página do modelo, e uma `.sheet` por página.
 *
 * A `.sheet` é 0,3 mm mais baixa que o papel. Uma caixa exatamente da altura da
 * página, com `break-after`, faz o Chrome às vezes arredondar para cima e
 * soltar uma folha em branco entre duas cheias. Os adesivos são posicionados a
 * partir do topo, então esse 0,3 mm só corta o que já estaria na borda do papel.
 */
const sheetCss = (layout, extra = '') => `
  @page {
    size: ${mm(layout.page.widthMm)} ${mm(layout.page.heightMm)};
    margin: 0;
  }
  * { box-sizing: border-box }
  html, body { margin: 0; padding: 0 }
  body {
    background: #fff;
    /* Preto puro por padrão: a cor de cada campo vem do modelo. */
    color: #000;
    font-family: Arial, Helvetica, sans-serif;
  }
  .sheet {
    position: relative;
    width: ${mm(layout.page.widthMm)};
    height: ${mm(Math.max(1, layout.page.heightMm - 0.3))};
    overflow: hidden;
    break-after: page;
    page-break-after: always;
  }
  .sheet:last-of-type { break-after: auto; page-break-after: auto }
  /*
   * No papel, tudo preto e sem traço fino — F211. O selo de garantia tinha
   * texto azul-marinho, vermelho e #1a1a1a: a térmica não tem cor, e cor vira
   * pontilhado que ninguém lê. Só na impressão: o editor continua mostrando a
   * cor que o modelo guardou.
   */
  @media print {
    .fld { color: #000 !important; background: transparent !important }
    .fld:not(.b) { font-weight: 600 !important }
    .fld-bars svg { fill: #000 !important }
  }
  ${extra}
  `;
/**
 * O título da folha de etiquetas (F210): um espaço de largura zero.
 *
 * O Chrome imprime o título da página no cabeçalho quando "Cabeçalhos e
 * rodapés" está marcado — e o título era o nome do modelo, que saía por cima
 * da etiqueta ("Selo garantia 4x10"). Vazio não serve: sem título o Chrome
 * imprime o endereço (`about:srcdoc`). A data do cabeçalho a página não
 * controla; a tela avisa para desmarcar a opção.
 */
export const BLANK_PRINT_TITLE = '\u200B';
const document_ = (title, css, body) => [
    '<!doctype html>',
    '<html lang="pt-BR"><head><meta charset="utf-8"/>',
    `<title>${escapeHtml(title)}</title>`,
    `<style>${css}</style>`,
    `</head><body>${body}</body></html>`,
].join('');
/**
 * A folha inteira, pronta para o `iframe`.
 *
 * Documento autocontido: nenhum `<link>`, nenhuma fonte externa, nenhum script.
 * Uma fonte que demore um instante a mais para carregar imprime a página com a
 * fonte errada, e ninguém vê a tempo de cancelar.
 *
 * A página é a FOLHA do modelo — A4 nos modelos da Elite —, com `columns ×
 * rows` adesivos cada, cada um no seu milímetro (`labelPosition`).
 */
export const renderLayoutSheetHtml = (layout, subjects, options = {}) => {
    const perSheet = Math.max(1, layout.columns * layout.rows);
    const skip = Math.min(Math.max(Math.trunc(options.skip ?? 0), 0), perSheet - 1);
    const cellsInOrder = [
        ...Array.from({ length: skip }, () => null),
        ...subjects,
    ];
    const context = { printedOn: options.printedOn ?? '' };
    const sheets = [];
    for (let start = 0; start < cellsInOrder.length; start += perSheet) {
        const cells = cellsInOrder
            .slice(start, start + perSheet)
            .map((subject, position) => subject === null
            ? `<div class="label" style="${cellStyle(layout, position)}"></div>`
            : renderLabel(layout, subject, position, start + position === skip ? options.highlight : undefined, options.interactive === true, context));
        sheets.push(`<div class="sheet">${cells.join('')}</div>`);
    }
    return document_(BLANK_PRINT_TITLE, sheetCss(layout, options.interactive === true ? '[data-element] { cursor: pointer }' : ''), sheets.join(''));
};
/**
 * A folha de teste de um modelo (F140): o contorno de cada adesivo, numerado.
 *
 * Imprime-se em papel comum e põe-se sobre a folha de adesivos, contra a luz.
 * Os contornos têm de cair nos recortes; se descem ou sobem fileira a fileira,
 * o erro é o vão entre fileiras, e se estão todos deslocados igual, é a margem.
 * A régua de 10 mm no primeiro adesivo separa isso do terceiro erro possível:
 * o diálogo de impressão escalando a página ("Escala: Padrão").
 *
 * Nenhum campo do modelo: o que se confere aqui é a folha, e um nome de produto
 * em cima do contorno esconderia justamente a borda.
 *
 * Numa bobina é o **Teste da bobina** (F264), impresso no próprio adesivo:
 * `pages` repete a fileira, e a segunda diz se o papel anda o que deve. O
 * último adesivo traz o papel que o driver tem de ter ("papel 107 × 21 mm") —
 * quem olha a foto do teste sabe o que conferir no Windows sem abrir o modelo.
 */
export const renderLayoutCalibrationHtml = (layout, options = {}) => {
    const perSheet = Math.max(1, layout.columns * layout.rows);
    const pages = Math.max(1, Math.trunc(options.pages ?? 1));
    const paper = `papel ${paperMm(layout.page.widthMm)} × ${paperMm(layout.page.heightMm)} mm`;
    const cells = Array.from({ length: perSheet }, (_, index) => [
        `<div class="label outline" style="${cellStyle(layout, index)}">`,
        `<div class="number">${String(index + 1)}</div>`,
        index === 0
            ? '<div class="ruler"><div class="ruler-bar"></div><div class="ruler-text">10 mm</div></div>'
            : '',
        index === perSheet - 1
            ? `<div class="paper">${escapeHtml(paper)}</div>`
            : '',
        '</div>',
    ].join(''));
    const sheet = `<div class="sheet">${cells.join('')}</div>`;
    /*
     * Na bobina o adesivo é a página inteira, e a `.sheet` é 0,3 mm mais baixa
     * que o papel: o traço de baixo, na borda, era cortado, e todo teste parecia
     * "cortou embaixo". O contorno vem para dentro, inteiro, e mais grosso para a
     * térmica de 203 dpi — alinhado, sobra a mesma folga nos quatro lados.
     */
    const rollOutline = isRollLayout(layout)
        ? '.outline { outline-width: 0.3mm; outline-offset: -0.7mm }'
        : '';
    const extra = `
  .outline { outline: 0.2mm solid #000; outline-offset: -0.1mm }
  .number {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: ${mm(Math.min(layout.label.heightMm, layout.label.widthMm) * 0.3)};
    font-weight: 700;
  }
  .ruler { position: absolute; left: 1mm; bottom: 1mm }
  /*
   * A barra é fundo preto, e o Chrome não imprime fundo com "Gráficos de plano
   * de fundo" desmarcado — saía só o contorno, e a régua parecia vazia (F264).
   */
  .ruler-bar {
    width: 10mm; height: 1mm; background: #000;
    border-left: 0.3mm solid #fff; border-right: 0.3mm solid #fff;
    outline: 0.2mm solid #000;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .ruler-text { font-size: 2mm }
  .paper {
    position: absolute; top: 0.8mm; right: 1mm;
    font-size: 2mm; font-weight: 700; white-space: nowrap;
  }
  ${rollOutline}
  `;
    return document_(BLANK_PRINT_TITLE, sheetCss(layout, extra), Array.from({ length: pages }, () => sheet).join(''));
};
//# sourceMappingURL=layout-html.js.map